import { Box } from '@mui/material';
import ButtonComponent from '../../component/button/button.component';
import i18n from '../../i18n';
import CardView from '../../component/card/card-view';
import TableComponent, { HeadCell } from '../../component/table/table.component';
import { useTranslation } from 'react-i18next';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import { ResellerState } from './reseller-management.state';
import { TopBarState } from '../../component/top-bar/top-bar-state';
import React, { useEffect } from 'react';
import { Reseller } from '../../models';
import ResellerUnit from './reseller-unit';
import CreateResellerDialog from './create-reseller.dialog';
import { ConditionType, SearchKeyInput, SearchKeyInputItems, ConditionsBox } from '../../component/form/search-key-input.component';
import { toJS } from 'mobx';
import './scss/reseller-management.style.scss'
import { formatEUR } from '../../constants/tool.fn';

// 经销商管理列表组件
const ResellerManagement = observer(() => {
    useTranslation();

    // 使用数据管理
    const resellerData = useLocalObservable(() => new ResellerState());
    // 顶部栏数据
    const topBarState = MobxObjectsManager.get(TopBarState);

    // 解构数据
    const {
        resellerList,
        isTableLoading,
        dataTotal,
        showUnit,
        enterResellerDetailObj,
        conditions
    } = resellerData;


    useEffect(() => {
        resellerData.init();
        topBarState.updateResellerTitle('');
        return () => {
            MobxObjectsManager.remove(ResellerState);
        }
    }, []);

    // 搜索ref
    const SearchKeyInputRef = React.useRef<{ searchKey: string, setSelectKey: (val: string) => void; }>();

    // 下拉框 左边搜索条件 静态数据
    const filterList: Array<FilterList> = [
        {
            value: 'nickname',
            label: i18n.t('general_text_name'),
        }, {
            value: 'phone_prefix_phone',
            label: i18n.t('general_text_phone')
        }, {
            value: 'city',
            label: i18n.t('group_management_page_text_city')
        }
    ];
    /**
     * 打开经销商详情
     * @param row
     */
    const handleOpenDetail = async (row: Reseller) => {
        resellerData.updateReseller(row)
        resellerData.setIsOpenDetail(true);
    }

    const tableHeadCell: HeadCell[] = [
        { id: 'nickname', label: i18n.t('general_text_name'), sortStatus: resellerData.getSortStatus('nickname') },
        { id: 'phone_prefix_phone', label: i18n.t('create_group_component_message_invalid_phone'), renderFunction: (row: Reseller) => `${row?.phone_prefix} ${(row?.phone || '')}` },
        { id: 'city', label: i18n.t('group_management_page_text_city'), sortStatus: resellerData.getSortStatus('city') },
        { id: 'reg_vat_number', label: i18n.t('restaurant_info_page_text_vat_number') },
        { id: 'balance', label: i18n.t('reseller_page_reseller_balance'), renderFunction: (row: Reseller) => formatEUR(Number(row?.balance ?? 0)), sortStatus: resellerData.getSortStatus('balance') },
        {
            id: 'group_detail', label: `${i18n.t('group_management_page_text_group')} / ${i18n.t('group_management_page_text_restaurant')}`,
            renderFunction: (row: Reseller) => {
                return `${(row?.responsible_group || []).length} / ${(row?.responsible_restaurant || []).length}`

            }
        },
        {
            id: 'reseller_discount', label: i18n.t('restaurant_info_page_text_discount_information'),
            sortStatus: resellerData.getSortStatus('reseller_discount'),
            renderFunction: (row: Reseller) => {
                return <>
                    {`${row?.reseller_discount ?? 100} %`}
                </>
            }
        },
        {
            id: 'operation', label: i18n.t('operation_record_operation_object_details'),
            renderFunction: (row: Reseller) => {
                return (
                    <ButtonComponent action='VIEW' handleClick={() => handleOpenDetail(row)} />
                )
            }
        }
    ];
    // *********************
    // Extra Function
    // *********************


    // 渲染集团列表组件
    const RenderComponent = (): JSX.Element => {
        const newFilterList = filterList as SearchKeyInputItems[];
        newFilterList.forEach((item) => {
            item.type = 'TEXT';
        })
        return (
            <Box>
                <Box className='table-top-tool'>
                    <Box className={'left-tool'}>
                        {/* 新增按钮 */}
                        <ButtonComponent
                            {...{
                                variant: 'contained',
                                size: 'small',
                                extraButtonStyle: 'add-button',
                                titleText: `${i18n.t('general_text_new')}`,
                                action: 'ADD',
                                space: false,
                                handleClick: () => {
                                    resellerData.openAddResellerDialog()
                                }
                            }}
                        />
                        <SearchKeyInput
                            ref={SearchKeyInputRef}
                            style={{ marginLeft: '8px' }}
                            items={newFilterList}
                            conditions={toJS(conditions)}
                            onSearchKeyChange={(key) => resellerData.setSelectValue(key)}
                            onSearch={async (conditions: ConditionType[]) => {
                                await resellerData.setConditions(conditions);
                            }}
                        />
                    </Box >
                </Box >
                <Box>
                    <ConditionsBox conditions={toJS(conditions)} onDelete={(conditions) => {
                        resellerData.setConditions(conditions);
                        SearchKeyInputRef?.current?.setSelectKey?.('');
                    }} />
                </Box>
                <TableComponent
                    {...{
                        showChecked: 1,
                        // 表格数据
                        list: resellerList ?? [],
                        // 开启表格自定义页脚
                        isUseCustomPagination: true,
                        // 表格头
                        headCellList: tableHeadCell,
                        // 表格加载
                        isTableLoading,
                        // 表格批量删除
                        handleDeleteAll: async (selected) => {
                            // 表格批量删除
                            await resellerData.handleDelete(selected, true);
                        },
                        // 表格排序
                        handleSort: (headCell) => {
                            resellerData.handleSort(headCell);
                        },
                        // 表格行可展开
                        hasExpand: false,
                        // 表格页脚
                        isPagination: true,
                        // 页脚表格数据总数
                        allCount: dataTotal,
                        // 开启折叠组件 防止选择展开按钮会框选到表格行复选框
                        isUseFold: true,
                        // 表格可编辑class类
                        extraClass: 'extraTable',
                    }}
                />

                {/* 新增经销商弹窗 */}
                <CreateResellerDialog useResellerData={resellerData} />

            </Box >
        )
    }

    // *********************
    // Render View
    // *********************

    return (
        showUnit ?
            (<ResellerUnit goBack={() => {
                resellerData.goBack();
            }} reseller={enterResellerDetailObj.resellerData} />)
            :
            (<CardView
                {...{
                    className: `reseller-management-page ${showUnit ? 'reseller-unit1' : ''} `,
                    contentRender: RenderComponent(),
                }}
            />)
    )
})

export default ResellerManagement;


// *********************
// TYPE
// *********************

// 下拉框类型
type FilterList = {
    value: string;
    label: string;
    disabled?: boolean;
}
